import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { Injectable, NgZone } from '@angular/core';
import { LoadScriptsService } from '@core/services/load-scripts/load-scripts.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { PlatformService } from '@core/services/platform.service';

import { ZendeskWindow } from './zendesk.models';
import { GtmService } from '@core/services/gtm/gtm.service';

@Injectable({
    providedIn: 'root'
})
export class ZendeskService {
    private windowRef: ZendeskWindow;
    // Optimization: Get the URL from field in Umbraco
    private zendeskScripturl = 'https://static.zdassets.com/ekr/snippet.js?key=46507ada-37a0-4d8b-b484-96eea500bfa7';

    private zendeskLoadingBs$ = new BehaviorSubject<boolean>(false);
    private zendeskChatIsOpenBs$ = new BehaviorSubject<boolean>(false);
    private zendeskScriptLoadedBs$ = new BehaviorSubject<boolean>(false);
    public zendeskLoading$: Observable<boolean> = this.zendeskLoadingBs$.asObservable();
    public zendeskChatIsOpen$: Observable<boolean> = this.zendeskChatIsOpenBs$.asObservable();
    public zendeskScriptLoaded$: Observable<boolean> = this.zendeskScriptLoadedBs$.asObservable();

    // Right now we only use this service to lazy load Zendesk, since we use their button once Zendesk is loaded.
    // It does expose some observables, which can be useful elsewhere if needed.
    constructor(
        private readonly loadscriptsService: LoadScriptsService,
        private readonly platformService: PlatformService,
        private readonly ngZone: NgZone,
        private readonly gtmService: GtmService,
        private readonly notificationService: NotificationService
    ) {
        if (this.platformService.isBrowser) {
            this.windowRef = window as unknown as ZendeskWindow;
        }
    }

    public openChat(): void {
        if (!this.platformService.isBrowser) {
            return;
        }
        this.zendeskScriptLoaded$.pipe(take(1)).subscribe(isLoaded => {
            this.gtmService.trackStartChat();

            if (!isLoaded) {
                this.loadChat();
                return;
            }
        });
    }

    private loadChat(): void {
        if (!this.windowRef) {
            return;
        }

        this.zendeskLoadingBs$.next(true);

        // Settings
        this.windowRef.zESettings = {
            webWidget: {
                contactOptions: {
                    enabled: true
                }
            }
        };

        // Because of our loadScriptService, this is kept as promise style for now
        this.loadscriptsService
            .loadScript(this.zendeskScripturl, {
                attributes: {
                    id: 'ze-snippet'
                }
            })
            .then(() => {
                // When scripts is loaded, zE should be available on window
                const zE = this.windowRef.zE;
                if (!zE) {
                    this.resetState();
                    return;
                }

                // Settings
                zE('messenger:set', 'locale', 'da');
                zE('messenger:set', 'cookies', true);

                zE('messenger:on', 'open', () => {
                    this.ngZone.run(() => {
                        this.onShow();
                    });
                });

                zE('messenger:on', 'close', () => {
                    this.ngZone.run(() => {
                        this.onHide();
                    });
                });

                zE('messenger', 'open');

                this.zendeskScriptLoadedBs$.next(true);
                this.zendeskLoadingBs$.next(false);
            })
            .catch(() => {
                this.resetState();
            });
    }

    private resetState(): void {
        this.notificationService.createNotification({
            fadeTime: 8000,
            translationText: 'customerService.zendesk.loadError',
            type: 'error'
        });
        this.zendeskLoadingBs$.next(false);
    }

    private onHide(): void {
        this.zendeskChatIsOpenBs$.next(false);
    }

    private onShow(): void {
        this.zendeskChatIsOpenBs$.next(true);
    }
}
